import React from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { isPlatformAdminHostname } from './config/platformAdminHost'

/** Legacy links pointed at /monitor-safe/ecdd — only /compliance-safe/ecdd is registered; unhandled path hit * → /login */
function MonitorSafeEcddDetailRedirect() {
  const { id } = useParams()
  return <Navigate to={`/compliance-safe/ecdd/${encodeURIComponent(id)}`} replace />
}
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { FeatureFlagProvider } from './contexts/FeatureFlagContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import HomePage from './pages/HomePage'

// Profile Safe components
import PatronDashboard from './pages/profile-safe/PatronDashboard'
import PatronTransactions from './pages/profile-safe/PatronTransactions'
import UnassignedTransactions from './pages/profile-safe/UnassignedTransactions'
import PatronConfiguration from './pages/profile-safe/PatronConfiguration'
import RulesBuilder from './pages/profile-safe/RulesBuilder'

// Monitor Safe components
import RuleTesting from './pages/monitor-safe/RuleTesting'
import MonitoringAnalytics from './pages/monitor-safe/MonitoringAnalytics'

// System & Master Data components
import ActivityLogs from './pages/admin/ActivityLogs'
import AuditTrail from './pages/admin/AuditTrail'
import DailyData from './pages/daily-data/DailyData'
import DimPatronFeed from './pages/daily-data/DimPatronFeed'
import DataManagement from './pages/daily-data/DataManagement'
import GamingSessionsFeed from './pages/daily-data/GamingSessionsFeed'
import MeterFeed from './pages/daily-data/MeterFeed'
import SlotAnalysisFeed from './pages/daily-data/SlotAnalysisFeed'
import DailyTurnover from './pages/daily-data/DailyTurnover'
import VisitorFeed from './pages/daily-data/VisitorFeed'
import EgmFeed from './pages/daily-data/EgmFeed'
import DailyDataSettings from './pages/daily-data/DailyDataSettings'
import Users from './pages/admin/Users'
import PermissionsAdmin from './pages/admin/Permissions'
import UserManagement from './pages/admin/UserManagement'
import PendingApprovals from './pages/admin/PendingApprovals'
import Profile from './pages/admin/Profile'

// Profile Safe components (exact implementation from 1_rxprofileweb)
import ProfileSearchContainer from './pages/profile-safe/ProfileSearchContainer'

// Monitor Safe components
import RuleBuilder from './pages/monitor-safe/RuleBuilder'
import CombinedRuleBuilder from './pages/monitor-safe/CombinedRuleBuilder'
import TransactionRuleBuilder from './pages/monitor-safe/TransactionRuleBuilder'
import GamingRuleBuilder from './pages/monitor-safe/GamingRuleBuilder'
import MonitorDashboard from './pages/monitor-safe/MonitorDashboard'
import AMLScreening from './pages/profile-safe/AMLScreening'
import WatchList from './pages/monitor-safe/WatchList'
import RuleScheduler from './pages/monitor-safe/RuleScheduler'
import RulesList from './pages/monitor-safe/RulesList'
import WatchListDetail from './pages/monitor-safe/WatchListDetail'
import GamingData from './pages/monitor-safe/GamingData'
import SampleDataGenerator from './pages/monitor-safe/SampleDataGenerator'
import MonitoringAlerts from './pages/monitor-safe/MonitoringAlerts'
import AlertDetail from './pages/monitor-safe/AlertDetail'
import SmrEcddRegistry from './pages/compliance-safe/SmrEcddRegistry'
import ECDDList from './pages/compliance-safe/ECDDList'
import ECDDDetail from './pages/compliance-safe/ECDDDetail'
import ScreeningApiKeys from './pages/compliance-safe/ScreeningApiKeys'
import PatronScreeningSettings from './pages/compliance-safe/PatronScreeningSettings'
import TtrRegistry from './pages/compliance-safe/TtrRegistry'
import TtrDetail from './pages/compliance-safe/TtrDetail'
import CompliancePeriodReports from './pages/compliance-safe/CompliancePeriodReports'
import GovernanceDashboard from './pages/governance-safe/GovernanceDashboard'
import RiskAssessment from './pages/governance-safe/RiskAssessment'
import RiskRegister from './pages/governance-safe/RiskRegister'
import RiskRegisterDetail from './pages/governance-safe/RiskRegisterDetail'
import ComplianceChecklist from './pages/governance-safe/ComplianceChecklist'
import RegulatoryObligations from './pages/governance-safe/RegulatoryObligations'
import TrainingTracker from './pages/governance-safe/TrainingTracker'
import Tasks from './pages/governance-safe/Tasks'
import BoardDashboard from './pages/governance-safe/BoardDashboard'

// PayoutSafe components
import PayoutRulesBuilder from './pages/payout-safe/PayoutRulesBuilder'
import PayoutMonitoring from './pages/payout-safe/PayoutMonitoring'
import PayoutCapture from './pages/payout-safe/PayoutCapture'
import PayoutMobileSimulate from './pages/payout-safe/PayoutMobileSimulate'
import PayoutMobileConfig from './pages/payout-safe/PayoutMobileConfig'

// Compliance Safe components
import ComplianceDashboard from './pages/compliance-safe/ComplianceDashboard'
import InvestigationList from './pages/compliance-safe/InvestigationList'
import IncidentMonitoring from './pages/compliance-safe/IncidentMonitoring'
import InvestigationCase from './pages/compliance-safe/InvestigationCase'
import InvestigationPipeline from './pages/compliance-safe/InvestigationPipeline'

// Profile Checks (new)
import ProfileChecks from './pages/profile-safe/ProfileChecks'
import KycRegistry from './pages/profile-safe/KycRegistry.jsx'
import PatronRiskConfiguration from './pages/governance-safe/PatronRiskConfiguration'
import VenueRiskAssessmentConfiguration from './pages/governance-safe/VenueRiskAssessmentConfiguration'
import TransactionRiskConfiguration from './pages/governance-safe/TransactionRiskConfiguration'
import ApprovalPolicies from './pages/governance-safe/ApprovalPolicies'
import ProfileOnboarding from './pages/profile-safe/ProfileOnboarding'
import VerificationQueue from './pages/profile-safe/VerificationQueue'
import VisitorManagement from './pages/profile-safe/VisitorManagement'

// Executive Dashboard
import ExecutiveDashboard from './pages/ExecutiveDashboard'

// Admin components
import GroupConfiguration from './pages/admin/GroupConfiguration'
import ClubOnboardingWizard from './pages/admin/ClubOnboardingWizard'
import PlatformConfiguration from './pages/admin/PlatformConfiguration'
import PlatformTenants from './pages/admin/PlatformTenants'
import BillableUsage from './pages/admin/BillableUsage'
import VenueConfiguration from './pages/admin/VenueConfiguration'
import Typologies from './pages/admin/Typologies'
import Employees from './pages/admin/Employees'
import EmployeeDetail from './pages/admin/EmployeeDetail'

function HomeRedirect() {
  return <Navigate to={isPlatformAdminHostname() ? '/admin/platform-config' : '/dashboard'} replace />
}

function App() {
  return (
    <FeatureFlagProvider>
    <AuthProvider>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Routes - wrapped in Layout */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <HomeRedirect />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><HomePage /></Layout>
            </ProtectedRoute>
          } />

          {/* Executive Dashboard */}
          <Route path="/executive" element={
            <ProtectedRoute>
              <Layout><ExecutiveDashboard /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Profile Safe Routes */}
          <Route path="/profile-safe/dashboard" element={
            <ProtectedRoute>
              <Layout><PatronDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/search" element={
            <ProtectedRoute>
              <Layout><ProfileSearchContainer /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/management" element={
            <ProtectedRoute>
              <Layout><PatronTransactions /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/issues" element={
            <ProtectedRoute>
              <Layout><UnassignedTransactions /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/onboarding-rules" element={
            <ProtectedRoute>
              <Layout><RulesBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/checks" element={
            <ProtectedRoute>
              <Layout><ProfileChecks /></Layout>
            </ProtectedRoute>
          } />
          {/* Legacy Profile Safe KYC Registry removed — use the Compliance KYC Registry. */}
          <Route path="/profile-safe/kyc-registry" element={<Navigate to="/compliance-safe/kyc-registry" replace />} />
          <Route path="/profile-safe/checks/aml-pep" element={
            <ProtectedRoute>
              <Layout><ProfileChecks initialTab="aml" /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/checks/dvs" element={
            <ProtectedRoute>
              <Layout><ProfileChecks initialTab="dvs" /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/config" element={
            <ProtectedRoute>
              <Layout><PatronConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/onboarding" element={
            <ProtectedRoute>
              <Layout><ProfileOnboarding /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/verification-queue" element={
            <ProtectedRoute>
              <Layout><VerificationQueue /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/visitors" element={
            <ProtectedRoute>
              <Layout><VisitorManagement /></Layout>
            </ProtectedRoute>
          } />

          {/* Compliance Safe Routes */}
          <Route path="/compliance-safe/dashboard" element={
            <ProtectedRoute>
              <Layout><ComplianceDashboard /></Layout>
            </ProtectedRoute>
          } />
          {/* Legacy Case Management removed — cases live under Investigations. */}
          <Route path="/compliance-safe/cases" element={<Navigate to="/compliance-safe/investigations" replace />} />
          <Route path="/compliance-safe/investigations" element={
            <ProtectedRoute>
              <Layout><InvestigationList /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/investigations/pipeline" element={
            <ProtectedRoute>
              <Layout><InvestigationPipeline /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/investigations/:id" element={
            <ProtectedRoute>
              <Layout><InvestigationCase /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/smr-registry" element={
            <ProtectedRoute>
              <Layout><SmrEcddRegistry /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/ecdd" element={
            <ProtectedRoute>
              <Layout><ECDDList /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/ecdd/:id" element={
            <ProtectedRoute>
              <Layout><ECDDDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/settings/patron-screening" element={
            <ProtectedRoute requiredPermission="compliance-safe/patron-screening:view">
              <Layout><PatronScreeningSettings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/settings/screening-keys" element={
            <ProtectedRoute requiredPermission="compliance-safe/patron-screening:view">
              <Layout><ScreeningApiKeys /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/ttr-registry" element={
            <ProtectedRoute>
              <Layout><TtrRegistry /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/ttr/:id" element={
            <ProtectedRoute>
              <Layout><TtrDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/ecdd" element={<Navigate to="/compliance-safe/ecdd" replace />} />
          <Route path="/monitor-safe/ecdd/:id" element={<MonitorSafeEcddDetailRedirect />} />
          <Route path="/compliance-safe/kyc-registry" element={
            <ProtectedRoute>
              <Layout><KycRegistry /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/incidents" element={
            <ProtectedRoute requiredPermission="compliance-safe/incidents:view">
              <Layout><IncidentMonitoring /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance-safe/period-reports" element={
            <ProtectedRoute requiredPermission="compliance-safe/period-reports:view">
              <Layout><CompliancePeriodReports /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Monitor Safe Routes */}
          <Route path="/monitor-safe/dashboard" element={
            <ProtectedRoute>
              <Layout><MonitorDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/alerts/:id" element={
            <ProtectedRoute>
              <Layout><AlertDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/alerts" element={
            <ProtectedRoute>
              <Layout><MonitoringAlerts /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe" element={
            <ProtectedRoute>
              <Layout><Navigate to="/governance-safe/dashboard" replace /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/dashboard" element={
            <ProtectedRoute>
              <Layout><GovernanceDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/obligations" element={
            <ProtectedRoute>
              <Layout><RegulatoryObligations /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/tasks" element={
            <ProtectedRoute>
              <Layout><Tasks /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/risk" element={
            <ProtectedRoute>
              <Layout><RiskAssessment /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/risk-assessment-config" element={
            <ProtectedRoute>
              <Layout><VenueRiskAssessmentConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/risk-register" element={
            <ProtectedRoute>
              <Layout><RiskRegister /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/risk-register/:id" element={
            <ProtectedRoute>
              <Layout><RiskRegisterDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/checklist" element={
            <ProtectedRoute>
              <Layout><ComplianceChecklist /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/training" element={
            <ProtectedRoute>
              <Layout><TrainingTracker /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/risk-config" element={
            <ProtectedRoute>
              <Layout><PatronRiskConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/transaction-risk-config" element={
            <ProtectedRoute>
              <Layout><TransactionRiskConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/approval-policies" element={
            <ProtectedRoute>
              <Layout><ApprovalPolicies /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/governance-safe/board-dashboard" element={
            <ProtectedRoute>
              <Layout><BoardDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/watch-list" element={
            <ProtectedRoute>
              <Layout><WatchList /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/watch-list/:memberId" element={
            <ProtectedRoute>
              <Layout><WatchListDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile-safe/aml-screening" element={
            <ProtectedRoute>
              <Layout><AMLScreening /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/rules" element={
            <ProtectedRoute>
              <Layout><RulesList /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/builder" element={
            <ProtectedRoute>
              <Layout><RuleBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/builder/transaction" element={
            <ProtectedRoute>
              <Layout><TransactionRuleBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/builder/gaming" element={
            <ProtectedRoute>
              <Layout><GamingRuleBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/builder/combined" element={
            <ProtectedRoute>
              <Layout><CombinedRuleBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/rule-scheduler" element={
            <ProtectedRoute>
              <Layout><RuleScheduler /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/testing" element={
            <ProtectedRoute>
              <Layout><RuleTesting /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/analytics" element={
            <ProtectedRoute>
              <Layout><MonitoringAnalytics /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/gaming-data" element={
            <ProtectedRoute>
              <Layout><GamingData /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/sample-data" element={
            <ProtectedRoute>
              <Layout><SampleDataGenerator /></Layout>
            </ProtectedRoute>
          } />

          {/* PayoutSafe Routes */}
          <Route path="/payout-safe/builder" element={
            <ProtectedRoute requiredPermission="payout-safe/builder:view">
              <Layout><PayoutRulesBuilder /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/payout-safe/capture" element={
            <ProtectedRoute requiredPermission="payout-safe/capture:view">
              <Layout><PayoutCapture /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/payout-safe/monitoring" element={
            <ProtectedRoute requiredPermission="payout-safe/monitoring:view">
              <Layout><PayoutMonitoring /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/payout-safe/mobile-simulate" element={
            <ProtectedRoute requiredPermission="payout-safe/mobile-simulate:view">
              <Layout><PayoutMobileSimulate /></Layout>
            </ProtectedRoute>
          } />
          {/* DEPRECATE: platform tenant → Mobile tab replaces this route */}
          <Route path="/payout-safe/mobile-config" element={
            <ProtectedRoute requiredPermission="payout-safe/builder:view">
              <Layout><PayoutMobileConfig /></Layout>
            </ProtectedRoute>
          } />
          
          {/* System & Master Data Routes (Admin only) */}
          <Route path="/admin/platform-config" element={
            <ProtectedRoute requiredPermission="platform:manage">
              <Layout><PlatformConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tenants" element={
            <ProtectedRoute requiredPermission="platform:manage">
              <Layout><PlatformTenants /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/billable-usage" element={
            <ProtectedRoute requiredPermission="platform:manage">
              <Layout><BillableUsage key="platform-billable" /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/usage" element={
            <ProtectedRoute requiredPermission="admin/billable-usage:view">
              <Layout><BillableUsage key="tenant-usage" /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/venue-config" element={
            <ProtectedRoute requiredPermission="admin/venue-config:manage">
              <Layout><VenueConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/typologies" element={
            <ProtectedRoute requiredPermission="admin/typologies:manage">
              <Layout><Typologies /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/group-config" element={
            <ProtectedRoute requiredPermission="admin/group-config:manage">
              <Layout><GroupConfiguration /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/club-onboarding" element={
            <ProtectedRoute requiredPermission="admin/group-config:manage">
              <Layout><ClubOnboardingWizard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/venue-onboarding" element={
            <Navigate to="/admin/club-onboarding" replace />
          } />
          <Route path="/admin/venue-payouts" element={
            <ProtectedRoute requiredPermission="payout-safe/builder:view">
              <Navigate to="/payout-safe/builder" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute requiredPermission="admin/user-management:view">
              <Layout><Users /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/permissions" element={
            <ProtectedRoute requiredPermission="admin/permissions:view">
              <Layout><PermissionsAdmin /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/user-management" element={
            <ProtectedRoute requiredPermission="admin/user-management:manage">
              <Layout><UserManagement /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/employees" element={
            <ProtectedRoute requiredPermission="admin/user-management:view">
              <Layout><Employees /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/employees/:id" element={
            <ProtectedRoute requiredPermission="admin/user-management:view">
              <Layout><EmployeeDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/activity" element={
            <ProtectedRoute requiredPermission="admin/activity:view">
              <Layout><ActivityLogs /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/daily-data" element={<Navigate to="/monitor-safe/daily-data" replace />} />
          <Route path="/daily-data/dimpatron" element={<Navigate to="/monitor-safe/daily-data/dimpatron" replace />} />
          <Route path="/daily-data/manage" element={<Navigate to="/monitor-safe/daily-data/manage" replace />} />
          <Route path="/monitor-safe/daily-data" element={
            <ProtectedRoute>
              <Layout><DailyData /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/dimpatron" element={
            <ProtectedRoute>
              <Layout><DimPatronFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/visitor" element={
            <ProtectedRoute>
              <Layout><VisitorFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/egm" element={
            <ProtectedRoute>
              <Layout><EgmFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/sessions" element={
            <ProtectedRoute>
              <Layout><GamingSessionsFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/meter" element={
            <ProtectedRoute>
              <Layout><MeterFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/slot-analysis" element={
            <ProtectedRoute>
              <Layout><SlotAnalysisFeed /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/turnover" element={
            <ProtectedRoute>
              <Layout><DailyTurnover /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/manage" element={
            <ProtectedRoute>
              <Layout><DataManagement /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/monitor-safe/daily-data/settings" element={
            <ProtectedRoute requiredPermission="admin/system:view">
              <Layout><DailyDataSettings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/imports" element={
            <Navigate to="/monitor-safe/daily-data/settings" replace />
          } />
          <Route path="/admin/audit" element={
            <ProtectedRoute requiredPermission="admin/audit:view">
              <Layout><AuditTrail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute requiredPermission="admin/approvals:view">
              <Layout><PendingApprovals /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/profile" element={
            <ProtectedRoute>
              <Layout><Profile /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              theme: {
                primary: 'green',
                secondary: 'black',
              },
            },
          }}
        />
      </div>
    </AuthProvider>
    </FeatureFlagProvider>
  )
}

export default App
